"use client"

import { Badge, Button } from '@yeshuman/sdk'
import { Sparkles } from 'lucide-react'
import { toast } from 'sonner'
import { useHiredSuggestionActions, type HiredSkillSuggestion } from '../../hooks/use-briefs'

export type BriefHiredSuggestionsProps = {
  briefId: string
  suggestions: HiredSkillSuggestion[]
}

/** Pending hired-signal proposals on the brief skills card. */
export function BriefHiredSuggestions({ briefId, suggestions }: BriefHiredSuggestionsProps) {
  const pending = suggestions.filter((suggestion) => suggestion.status === 'pending')
  const { acceptSuggestion, dismissSuggestion, busyId, busyAction } = useHiredSuggestionActions(briefId)

  if (pending.length === 0) return null

  return (
    <div className="mt-4 space-y-3 border-t border-border pt-4" data-testid="hired-skill-suggestions">
      {pending.map((suggestion) => {
        const busy = busyId === suggestion.id
        const accepting = busy && busyAction === 'accept'
        return (
          <div key={suggestion.id} className="space-y-3" data-testid="hired-skill-suggestion">
            <div className="flex items-start gap-2">
              <Sparkles className="mt-0.5 size-4 shrink-0 text-muted-foreground" aria-hidden />
              <div className="space-y-1">
                <p className="text-sm font-medium text-foreground">
                  {suggestion.candidate_name} was hired
                </p>
                <p className="text-sm text-muted-foreground">
                  Add these skills as preferred on this brief?
                </p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {suggestion.skill_names.map((name) => (
                <Badge key={name} variant="outline" data-testid="hired-suggestion-skill">
                  {name}
                </Badge>
              ))}
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                size="sm"
                disabled={busy}
                data-testid="hired-suggestion-accept"
                onClick={() =>
                  acceptSuggestion(suggestion.id, {
                    onSuccess: () => {
                      toast.success(`Added preferred skills from ${suggestion.candidate_name}`)
                    },
                    onError: (error: Error) => {
                      toast.error(error.message || 'Failed to accept suggestion')
                    },
                  })
                }
              >
                {accepting ? 'Saving…' : 'Accept'}
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={busy}
                data-testid="hired-suggestion-dismiss"
                onClick={() =>
                  dismissSuggestion(suggestion.id, {
                    onSuccess: () => {
                      toast.success('Suggestion dismissed')
                    },
                    onError: (error: Error) => {
                      toast.error(error.message || 'Failed to dismiss suggestion')
                    },
                  })
                }
              >
                Dismiss
              </Button>
            </div>
          </div>
        )
      })}
    </div>
  )
}
